import * as React from "react";
import { Field, Form, Formik, FormikValues } from "formik";
import { LabelValue } from "components/LabelValue/LabelValue";
import {
  AdditionalProjectContact,
  ApiError,
  Client,
  ClientConfig,
  ClientsService,
  SystemSettingsService
} from "gen/clients";
import { useMutation, useQuery } from "react-query";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { TextInputField } from "../../../../../components/formikFields/TextInputField/TextInputField";
import { Accordion, AccordionDetails, AccordionSummary, Box, Skeleton, Stack, Typography } from "@mui/material";
import { ProjectContactsPanel } from "./ProjectContactsPanel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isValidEmail } from "utils/emailUtils";
import { validateIPOrCIDRList } from "utils/stringUtils";
import { CheckboxWithLabel } from "formik-mui";
import { LogoField } from "./components/LogoField";
import { SignUpUrlField } from "./components/SignUpUrlField";
import { AppBundleField } from "./components/AppBundleField";
import { SelectOneField } from "../../../../../components/formikFields/SelectOneField/SelectOneField";
import { BusinessAddress } from "./components/BusinessAddress";
import { useIsSuperUser } from "hooks/useIsSuperUser";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  client: Client;
  clientConfig: ClientConfig;
  onSaveSuccess: () => void;
}

enum FieldName {
  emailDomains = "emailDomains",
  ipRestriction = "ipRestriction",
  externalGroups = "externalGroups",
  bccEmailNotifications = "bccEmailNotifications",
  disableSelfRegistration = "disableSelfRegistration",
  isOrgManagementEnabled = "isOrgManagementEnabled",
  isMfaEnabled = "isMfaEnabled",
  skipAddressOnSignUp = "skipAddressOnSignUp",
  chatbotHelpCenter = "chatbotHelpCenter",
  clientContactEntityViewScope = "clientContactEntityViewScope",
  isConfigLocked = "isConfigLocked"
}

enum ChatbotHelpCenterOption {
  SYSTEM_DEFAULT = "SYSTEM_DEFAULT",
  ENABLED = "ENABLED",
  DISABLED = "DISABLED"
}

interface FormValues {
  [FieldName.emailDomains]: string;
  [FieldName.ipRestriction]: string;
  [FieldName.externalGroups]: string;
  [FieldName.bccEmailNotifications]: string;
  [FieldName.disableSelfRegistration]: boolean;
  [FieldName.isOrgManagementEnabled]: boolean;
  [FieldName.isMfaEnabled]: boolean;
  [FieldName.skipAddressOnSignUp]: boolean;
  [FieldName.chatbotHelpCenter]: ChatbotHelpCenterOption;
  [FieldName.clientContactEntityViewScope]: ClientConfig.clientContactEntityViewScope;
  [FieldName.isConfigLocked]: boolean;
}

const GeneralInformationTab: React.FC<Props> = ({ client, clientConfig, onSaveSuccess }) => {
  const [additionalProjectContacts, setAdditionalProjectContacts] = React.useState<AdditionalProjectContact[]>(
    clientConfig.additionalProjectContacts || []
  );
  const isSuperUser = useIsSuperUser();
  const isReadOnly = useClientReadOnly();

  const { data: systemSettings } = useQuery("getSystemSettings", {
    queryFn: SystemSettingsService.getSystemSettings
  });

  const { data: externalIdentityProviders, isLoading: isExternalIdentityProvidersLoading } = useQuery(
    ["listClientExternalIdentityProviders", client.id],
    {
      queryFn: () => ClientsService.listClientExternalIdentityProviders({ id: client.id }),
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  const { data: businessAddress, isLoading: isBusinessAddressLoading } = useQuery(
    ["getClientBusinessAddress", client.id],
    {
      queryFn: () => ClientsService.getClientBusinessAddress({ clientId: client.id })
    }
  );

  const lltsBranchName = React.useMemo(() => {
    if (!clientConfig.lltsBranchId || !systemSettings?.lltsBranches) {
      return undefined;
    }
    return systemSettings.lltsBranches.find(branch => branch.id === clientConfig.lltsBranchId)?.name;
  }, [clientConfig.lltsBranchId, systemSettings?.lltsBranches]);

  const chatbotHelpCenterOption = React.useMemo(() => {
    if (clientConfig.isChatbotHelpCenterEnabled === undefined) {
      return ChatbotHelpCenterOption.SYSTEM_DEFAULT;
    }
    return clientConfig.isChatbotHelpCenterEnabled ? ChatbotHelpCenterOption.ENABLED : ChatbotHelpCenterOption.DISABLED;
  }, [clientConfig.isChatbotHelpCenterEnabled]);

  const systemChatbotHelpCenterLabel = systemSettings?.isChatbotHelpCenterEnabled ? "Enabled" : "Disabled";

  const {
    mutate,
    isError: isMutateError,
    error: mutateError,
    isLoading: isMutating
  } = useMutation("updateClientConfig", {
    mutationFn: clientConfig ? ClientsService.updateClientConfig : ClientsService.createClientConfig,
    onSuccess: onSaveSuccess
  });

  const validate = React.useCallback(
    (formValues: FormikValues) => {
      const errors: Record<string, string> = {};
      if (formValues.emailDomains) {
        const domains = (formValues.emailDomains as string).split(/\s*,\s*/);
        domains.forEach(domain => {
          if (!domain.includes(".")) {
            errors.emailDomains = `Invalid domain name "${domain}"`;
          }
          if (systemSettings?.notAllowedEmailDomains?.includes(domain)) {
            errors.emailDomains = `Domain ${domain} is not allowed`;
          }
        });
      }
      if (formValues.ipRestriction) {
        const ipList = (formValues.ipRestriction as string)
          .split(/\s*,\s*/)
          .map(ip => ip.trim())
          .filter(ip => ip.length > 0);
        if (!validateIPOrCIDRList(ipList)) {
          errors.ipRestriction = "Invalid IP address or CIDR notation";
        }
      }
      if (formValues.bccEmailNotifications) {
        const bccEmails = (formValues.bccEmailNotifications as string).split(/\s*,\s*/);
        bccEmails.forEach(email => {
          if (!isValidEmail(email)) {
            errors.bccEmailNotifications = `Invalid email "${email}"`;
          }
        });
      }
      return errors;
    },
    [systemSettings]
  );

  const onSubmit = React.useCallback(
    async (formValues: FormValues) => {
      const clientConfigUpdate: ClientConfig = clientConfig
        ? { ...clientConfig, additionalProjectContacts }
        : { id: client.id };
      const emailDomains = formValues[FieldName.emailDomains]
        .split(/\s*,\s*/)
        .map(d => d.trim())
        .filter(d => d.length > 0);
      const ipAddresses = formValues[FieldName.ipRestriction]
        .split(/\s*,\s*/)
        .map(d => d.trim())
        .filter(d => d.length > 0);
      const externalGroups = formValues[FieldName.externalGroups]
        .split(/\s*,\s*/)
        .map(d => d.trim())
        .filter(d => d.length > 0);
      clientConfigUpdate.allowedEmailDomains = emailDomains.length > 0 ? emailDomains : undefined;
      clientConfigUpdate.ipAddressWhitelist = ipAddresses.length > 0 ? ipAddresses : undefined;
      clientConfigUpdate.externalGroups = externalGroups.length > 0 ? externalGroups : undefined;
      const bccEmailAddresses = formValues[FieldName.bccEmailNotifications]
        .split(/\s*,\s*/)
        .map(d => d.trim())
        .filter(d => d.length > 0);
      clientConfigUpdate.bccEmailNotifications = bccEmailAddresses.length > 0 ? bccEmailAddresses : undefined;
      clientConfigUpdate.disableSelfRegistration = formValues[FieldName.disableSelfRegistration];
      clientConfigUpdate.isOrgManagementEnabled = formValues[FieldName.isOrgManagementEnabled];
      clientConfigUpdate.isMfaEnabled = formValues[FieldName.isMfaEnabled];
      clientConfigUpdate.skipAddressOnSignUp = formValues[FieldName.skipAddressOnSignUp];
      const chatbotHelpCenter = formValues[FieldName.chatbotHelpCenter];
      clientConfigUpdate.isChatbotHelpCenterEnabled =
        chatbotHelpCenter === ChatbotHelpCenterOption.SYSTEM_DEFAULT
          ? undefined
          : chatbotHelpCenter === ChatbotHelpCenterOption.ENABLED;
      clientConfigUpdate.clientContactEntityViewScope = formValues[FieldName.clientContactEntityViewScope];
      // Only a Super Admin can change the lock. For everyone else the existing value is carried
      // through unchanged by the spread above, so a regular admin's save can never clear it.
      if (isSuperUser) {
        clientConfigUpdate.isConfigLocked = formValues[FieldName.isConfigLocked];
      }
      await mutate({
        id: `${client.id}`,
        requestBody: clientConfigUpdate
      });
    },
    [clientConfig, client.id, additionalProjectContacts, mutate, isSuperUser]
  );

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          [FieldName.emailDomains]: clientConfig?.allowedEmailDomains?.join(", ") || "",
          [FieldName.ipRestriction]: clientConfig?.ipAddressWhitelist?.join(", ") || "",
          [FieldName.externalGroups]: clientConfig?.externalGroups?.join(", ") || "",
          [FieldName.bccEmailNotifications]: clientConfig?.bccEmailNotifications?.join(", ") || "",
          [FieldName.disableSelfRegistration]: clientConfig.disableSelfRegistration || false,
          [FieldName.isOrgManagementEnabled]: clientConfig.isOrgManagementEnabled || false,
          [FieldName.isMfaEnabled]: clientConfig.isMfaEnabled || false,
          [FieldName.skipAddressOnSignUp]: clientConfig.skipAddressOnSignUp || false,
          [FieldName.chatbotHelpCenter]: chatbotHelpCenterOption,
          [FieldName.clientContactEntityViewScope]:
            clientConfig.clientContactEntityViewScope || ClientConfig.clientContactEntityViewScope.ALL,
          [FieldName.isConfigLocked]: clientConfig.isConfigLocked || false
        }}
        onSubmit={onSubmit}
        validate={validate}
        validateOnChange={false}
        validateOnBlur={true}
      >
        <Form>
          <Stack spacing={2}>
            <Stack direction="row" spacing={4} alignItems="flex-start">
              <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                <LabelValue label="Client Name" value={clientConfig.companyName} />
                <LabelValue label="Client ID" value={client.id} />
                {isSuperUser && (
                  <LabelValue
                    label="Lock Account"
                    value={<Field name={FieldName.isConfigLocked} type="checkbox" component={CheckboxWithLabel} />}
                    info="When locked, only System Admins can make changes to this client's configuration."
                  />
                )}
                <LabelValue label="LanguageLine Branch" value={lltsBranchName || "Not assigned"} />
                <LabelValue label="Status" value={client.status} />
                <LabelValue
                  label="SSO"
                  value={
                    isExternalIdentityProvidersLoading ? (
                      <Skeleton variant="rounded" animation="wave" width={48} />
                    ) : externalIdentityProviders?.length ? (
                      "Yes"
                    ) : (
                      "No"
                    )
                  }
                />
                {clientConfig.salesRepresentative && (
                  <LabelValue
                    label="Sales Representative"
                    value={`${clientConfig.salesRepresentative.firstName} ${clientConfig.salesRepresentative.lastName} (${clientConfig.salesRepresentative.email})`}
                  />
                )}
              </Stack>
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={theme => ({ color: theme.palette.grey["700"], fontWeight: 600 })}>
                  Business Address:
                </Typography>
                {isBusinessAddressLoading ? (
                  <Stack spacing={0.5}>
                    <Skeleton variant="rounded" animation="wave" width={220} />
                    <Skeleton variant="rounded" animation="wave" width={180} />
                    <Skeleton variant="rounded" animation="wave" width={200} />
                  </Stack>
                ) : (
                  <BusinessAddress address={businessAddress} />
                )}
              </Stack>
            </Stack>
            <AppBundleField
              clientConfig={clientConfig}
              onSaveSuccess={onSaveSuccess}
              disabled={isMutating || isReadOnly}
            />
            <LogoField clientConfig={clientConfig} onSaveSuccess={onSaveSuccess} disabled={isMutating || isReadOnly} />
            <SignUpUrlField
              clientConfig={clientConfig}
              onSaveSuccess={onSaveSuccess}
              disabled={isMutating || isReadOnly}
            />
            <Stack direction="row" spacing={4}>
              <LabelValue
                label="Disable Self-Registration"
                value={
                  <Field
                    name={FieldName.disableSelfRegistration}
                    type="checkbox"
                    component={CheckboxWithLabel}
                    disabled={isReadOnly}
                  />
                }
              />
              <LabelValue
                label="Skip Address Step on Sign Up"
                value={
                  <Field
                    name={FieldName.skipAddressOnSignUp}
                    type="checkbox"
                    component={CheckboxWithLabel}
                    disabled={isReadOnly}
                  />
                }
                info="When enabled, users will not be asked to enter an address during sign up."
              />
            </Stack>
            <Stack direction="row" spacing={4}>
              <LabelValue
                label="Enable User Management by Org Admins"
                value={
                  <Field
                    name={FieldName.isOrgManagementEnabled}
                    type="checkbox"
                    component={CheckboxWithLabel}
                    disabled={isReadOnly}
                  />
                }
              />
              <LabelValue
                label="Enable MFA"
                value={
                  <Field
                    name={FieldName.isMfaEnabled}
                    type="checkbox"
                    component={CheckboxWithLabel}
                    disabled={isReadOnly}
                  />
                }
              />
            </Stack>
            <LabelValue
              label="AI Chatbot Help Center"
              info="Determines whether the AI Chatbot Help Center is available to the client's users in the Portal. When set to use the system setting, the value configured in System Settings applies."
              value={
                <SelectOneField
                  name={FieldName.chatbotHelpCenter}
                  disabled={isReadOnly}
                  options={[
                    {
                      label: `Use System Setting (currently ${systemChatbotHelpCenterLabel})`,
                      value: ChatbotHelpCenterOption.SYSTEM_DEFAULT
                    },
                    { label: "Enabled", value: ChatbotHelpCenterOption.ENABLED },
                    { label: "Disabled", value: ChatbotHelpCenterOption.DISABLED }
                  ]}
                />
              }
            />
            <LabelValue
              label="New Client Contact View Scope"
              info="This setting determines the default view scope for new client contacts. Existing client contacts will not be affected."
              value={
                <SelectOneField
                  name={FieldName.clientContactEntityViewScope}
                  disabled={isReadOnly}
                  options={[
                    { label: "All Projects/Quotes", value: ClientConfig.clientContactEntityViewScope.ALL },
                    { label: "Own Projects/Quotes", value: ClientConfig.clientContactEntityViewScope.OWN }
                  ]}
                />
              }
            />
            <LabelValue
              label="Allowed Email Domains"
              value={
                <TextInputField
                  name={FieldName.emailDomains}
                  label=""
                  disabled={isReadOnly}
                  helperText="Comma-separated list of domains. Example: llts.com, chase.com"
                />
              }
            />
            <LabelValue
              label="IP Restriction"
              value={
                <TextInputField
                  name={FieldName.ipRestriction}
                  label=""
                  disabled={isReadOnly}
                  helperText="Comma-separated list of IP addresses or CIDR notations. Example: 192.168.1.1, 10.0.0.0/24"
                />
              }
            />
            <LabelValue
              label="External Groups"
              info="Groups in the external client's system that help to identify users belonging to the client. Users that have a matching externalGroup Cognito atrribute value will be associated with the client."
              value={
                <TextInputField
                  name={FieldName.externalGroups}
                  disabled={isReadOnly}
                  helperText="Comma-separated list of external group names"
                />
              }
            />
            <LabelValue
              label="BCC Client Email Notifications"
              value={
                <TextInputField
                  name={FieldName.bccEmailNotifications}
                  label=""
                  disabled={isReadOnly}
                  helperText="Please enter a comma-separated list of email addresses that will be blind-copied on all email notifications sent to the client"
                />
              }
            />
            <Box sx={{ pt: 2 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack>
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                      Additional Project Contacts
                    </Typography>
                    <Typography variant="body2">
                      Configure client contacts that will be notified about all changes to the client&apos;s projects
                      and quotes
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <ProjectContactsPanel
                    client={client}
                    contactList={additionalProjectContacts}
                    setContactList={setAdditionalProjectContacts}
                    disabled={isReadOnly}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Stack>
          <Box sx={{ mt: 2 }}>
            {isMutateError && <ApiErrorMessage apiError={mutateError as ApiError} />}
            <LoadingButton isLoading={isMutating} disabled={isReadOnly}>
              Save
            </LoadingButton>
          </Box>
        </Form>
      </Formik>
    </>
  );
};
export { GeneralInformationTab };
